import { ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { usePagination } from '@/lib/v3/components/Table';
import { OrderColumnState } from '@/lib/v3/components/Table/Table';

import { useStateUserContext } from '@/src/application/context/UserContext';
import DateConverter from '@/src/application/util/DateConverter';
import { GetJourneysListPayload } from '@/src/infrastructure/interfaces/IAutomation.interface';
import { Status } from '@/src/infrastructure/interfaces/IIntegrations.interface';
import { useAutomationService } from '@/src/infrastructure/Protocol/Automation/useAutomationService';
import { Journey } from '@/src/modules/AutomationModuleNew/types/flowsTypes';

import { CheckboxItems } from '@/modules/AutomationModuleNew/components/DropdownFilterSection/DropdownFilterSection';

const defaultFilters: GetJourneysListPayload = {
  search: '',
  status: [],
  page: 1,
  limit: 10,
  creationDateBefore: null,
  creationDateAfter: null,
  sortField: 'creationDate',
  sortOrder: 'desc',
};

export const useJourneysListFilters = () => {
  const service = useAutomationService();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [countDataList, setCountDataList] = useState<{ total: number; partial: number }>({
    total: 0,
    partial: 0,
  });
  const [hasFiltersApplied, setHasFiltersApplied] = useState(false);
  const { userData } = useStateUserContext();

  const defaultDropDownItems = useCallback(
    (statusUrl?: string | null): CheckboxItems => {
      const statusUrlSplit = statusUrl?.split(',') as Status[];
      return {
        status: {
          label: t('RecurringMessagesFilters.filter.status'),
          items: [
            {
              id: 'active',
              label: t('useJourneysMessageFilters.filter.active'),
              value: 'active',
              checked: statusUrlSplit?.includes('active') || false,
            },
            {
              id: 'inactive',
              label: t('useJourneysMessageFilters.filter.inactive'),
              value: 'inactive',
              checked: statusUrlSplit?.includes('inactive') || false,
            },
            {
              id: 'draft',
              label: t('useJourneysMessageFilters.filter.draft'),
              value: 'draft',
              checked: statusUrlSplit?.includes('draft') || false,
            },
          ],
        },
      };
    },
    [t]
  );

  const [dropDownItems, setDropDownItems] = useState<CheckboxItems>(
    defaultDropDownItems(searchParams.get('status'))
  );

  const {
    data,
    setData,
    isLoading,
    valuePagination,
    setRowCount,
    rowCount,
    paginationChange,
    orderColumn,
    orderColumnChange,
    rowSelection,
    rowSelectionChange,
  } = usePagination<Journey>({
    initialPaginationState: {
      currentPage: searchParams.get('page')
        ? Number(searchParams.get('page'))
        : defaultFilters.page,
      pageSize: searchParams.get('limit')
        ? Number(searchParams.get('limit'))
        : (defaultFilters.limit as number),
    },
    initialOrderColumn: {
      orderByColumn: searchParams.get('sortField') || (defaultFilters.sortField as string),
      orderByDirection:
        (searchParams.get('sortOrder') as 'asc' | 'desc') ||
        (defaultFilters.sortOrder as 'asc' | 'desc'),
    },
  });

  const [filters, setFilters] = useState<GetJourneysListPayload>({
    search: searchParams.get('search') || '',
    status: searchParams.get('status')?.split(',') as Status[],
    ...(searchParams.get('creationDateAfter') && {
      creationDateAfter: new Date(searchParams.get('creationDateAfter') as string),
    }),
    ...(searchParams.get('creationDateBefore') && {
      creationDateBefore: new Date(searchParams.get('creationDateBefore') as string),
    }),
    page: valuePagination.currentPage,
    limit: valuePagination.pageSize,
    sortField: searchParams.get('sortField') || 'creationDate',
    sortOrder: searchParams.get('sortOrder') || 'desc',
  });

  const getHasFiltersApplied = useCallback((filters: GetJourneysListPayload) => {
    return (
      filters.search !== '' ||
      (filters.status && filters.status.length > 0) ||
      filters.creationDateAfter instanceof Date ||
      filters.creationDateBefore instanceof Date
    );
  }, []);

  const getData = useCallback(
    async (filters: GetJourneysListPayload) => {
      const response = await service.getJourneysList(filters);

      if (response.success) {
        const {
          data: { result, pager },
        } = response;
        const partial = pager.countPartial ?? 0;
        const total = pager.countTotal ?? 0;
        const hasFiltersApplied = getHasFiltersApplied(filters);
        setHasFiltersApplied(hasFiltersApplied);
        setCountDataList({ total, partial });
        setData(result);
        setRowCount(hasFiltersApplied ? partial : total);
      }
    },
    [getHasFiltersApplied, service, setData, setRowCount]
  );

  const handlePaginationChange = (currentPage: number, pageSize: number) => {
    void paginationChange(currentPage, pageSize, async () => {
      const newFilters = { ...filters, page: currentPage, limit: pageSize };
      updateFiltersUrl(newFilters);
      await getData(newFilters);
    });
  };

  const updateFiltersUrl = useCallback(
    (newFilters: GetJourneysListPayload) => {
      const updatedFilters = { ...filters, ...newFilters };
      setFilters(updatedFilters);

      const mapDates = {
        ...updatedFilters,
        startDate: updatedFilters.creationDateAfter?.toISOString(),
        endDate: updatedFilters.creationDateBefore?.toISOString(),
      };
      const params = new URLSearchParams();
      Object.entries(mapDates).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) params.set(key, value.join(','));
        } else if (value) {
          params.set(key, String(value));
        }
      });

      setSearchParams(params);
    },
    [filters, setSearchParams]
  );

  const updateUrlAndFetchDataWithResetPagination = useCallback(
    async (updateFilters: GetJourneysListPayload, noResetPageAndLimit: boolean = false) => {
      const newFilters = {
        ...filters,
        ...updateFilters,
        ...(!noResetPageAndLimit && { page: defaultFilters.page }),
        ...(!noResetPageAndLimit && { limit: defaultFilters.limit }),
      };
      updateFiltersUrl(newFilters);
      await paginationChange(newFilters.page as number, newFilters.limit as number, async () => {
        await getData(newFilters);
      });
    },
    [filters, getData, paginationChange, updateFiltersUrl]
  );

  const getInitialData = useCallback(async () => {
    await updateUrlAndFetchDataWithResetPagination(filters);
  }, [filters, updateUrlAndFetchDataWithResetPagination]);

  const resetFilters = useCallback(async () => {
    await updateUrlAndFetchDataWithResetPagination(defaultFilters);
    setDropDownItems(defaultDropDownItems());
  }, [defaultDropDownItems, updateUrlAndFetchDataWithResetPagination]);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFilters((prevState) => ({ ...prevState, search: value }));
  }, []);

  const handleSearchApply = useCallback(
    (value: string) => {
      const newFilters = {
        ...filters,
        search: value,
      };
      void updateUrlAndFetchDataWithResetPagination(newFilters);
    },
    [filters, updateUrlAndFetchDataWithResetPagination]
  );

  const handleStatusFilterApply = useCallback(
    (checkedItems?: CheckboxItems) => {
      const newFilters = {
        ...filters,
        status:
          (checkedItems?.status?.items.map((item) => item.value.toString()) as Status[]) || [],
      };
      void updateUrlAndFetchDataWithResetPagination(newFilters);
    },
    [filters, updateUrlAndFetchDataWithResetPagination]
  );

  const handleStatusFilterChange = useCallback(
    (isChecked: boolean, section: string, index: number) => {
      setDropDownItems((prevState) => {
        const newItems = { ...prevState };
        newItems[section].items[index].checked = isChecked;
        return newItems;
      });
    },
    []
  );

  const handleDateChange = useCallback(
    (date: Date | [Date | null, Date | null] | null) => {
      if (Array.isArray(date)) {
        const { timeOffset } = userData;
        const formatGMT = timeOffset ?? 0;
        let startDate = date[0];
        if (startDate) {
          startDate = DateConverter.toGMTDate(startDate.toISOString(), formatGMT);
        }

        let endDate = date[1];
        if (endDate) {
          endDate.setHours(23, 59, 59, 999);
          endDate = DateConverter.toGMTDate(endDate.toISOString(), formatGMT);
        }

        setFilters((prevState) => ({
          ...prevState,
          creationDateAfter: startDate,
          creationDateBefore: endDate,
        }));
        if (endDate) {
          void updateUrlAndFetchDataWithResetPagination({
            ...filters,
            creationDateAfter: startDate,
            creationDateBefore: endDate,
          });
        }
      }
    },
    [filters, updateUrlAndFetchDataWithResetPagination, userData]
  );

  const handleOrderColumnChange = useCallback(
    (value: OrderColumnState) => {
      const newFilters = {
        ...filters,
        sortField: value.orderByColumn,
        sortOrder: value.orderByDirection,
      };

      void orderColumnChange(value, async () => {
        await updateUrlAndFetchDataWithResetPagination(newFilters, true);
      });
    },
    [filters, orderColumnChange, updateUrlAndFetchDataWithResetPagination]
  );

  const totalRowSelected = Object.keys(rowSelection).length || 0;
  const dataSelected = Object.keys(rowSelection).map((index) => data[Number(index)]);

  return {
    dropDownItems,
    data,
    countDataList,
    getInitialData,
    valuePagination,
    rowCount,
    handlePaginationChange,
    isLoading,
    hasFiltersApplied,
    resetFilters,
    filters,
    handleSearchChange,
    handleSearchApply,
    handleStatusFilterApply,
    handleStatusFilterChange,
    handleDateChange,
    orderColumn,
    handleOrderColumnChange,
    rowSelection,
    rowSelectionChange,
    totalRowSelected,
    dataSelected,
    setDataTableIntegration: setData,
  };
};
